import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { debounce } from "lodash";

import type { IChipItem } from "@similarweb/ui-components";
import {
    Button,
    IconButton,
    ButtonChipItem,
    ChipContainer,
    SearchInput,
    PlainTooltip,
} from "@similarweb/ui-components";

import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import DurationService from "app/services/DurationService";
import { DefaultFetchService, NoCacheHeaders } from "app/services/fetchService";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { allTrackers } from "app/services/track/track";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IMarketingWorkspaceTotalTrafficShareProps } from "./MarketingWorkspaceTotalTrafficShare";
import { MarketingWorkspaceTotalTrafficShare } from "./MarketingWorkspaceTotalTrafficShare";
import {
    OptionButton,
    OptionButtonsContainer,
    RECOMMENDATIONS_BODY_HEIGHT,
    MonitorKeywordsTableTopBarContainer,
    SearchContainerWrapper,
    Right,
    MonitorKeywordsSuggestedKeywordsContainer,
    RecommendationsBody,
    TooltipContentContainer,
    TooltipColumn,
    BoxTitleContainer,
    TotalTrafficShareContainer,
    BoxTitleRightAlignedContainer,
    RecommendationsLoaderContainer,
    TooltipValue,
    PixelPlaceholderLoaderStyled,
    TooltipColumnSeparator,
} from "./styledComponents";

const LOADER_HORIZONTAL_MARGIN = 24;
const NUMBER_OF_LOADERS = 10;

interface IRecommendation {
    cpc: string;
    volume: string;
    keyword: string;
}

interface IMonitorKeywordsTableTopBarProps {
    onFilterChange: (any, boolean) => void;
    onAddKeywordClick: (keywords: string[], index: number) => () => void;
    onRefreshSuggestions: () => void;
    isLoadingData?: boolean;
    Keys?: string[];
    filtersStateObject: any;
    keywordsType: string;
    endpoint: string;
    permissionsComponent: string;
    searchPlaceholder: string;
    totalTrafficTitle: string;
    tooltipTitle: string;
    isRecommendationLoading: boolean;
    isRecommendationOpen: boolean;
    recommendations: IRecommendation[];
    isUsersGroup?: boolean;
    keywordGroup: IUserKeywordGroup;
    onClickRecommended?: VoidFunction;
    isPartnerPage?: boolean;
    excelConfig: {
        url: string;
        requestBody: {
            country: number;
            KeywordsGroup: string;
            sitesFilterExact: string[];
        };
    };
}

export const MonitorKeywordsTableTopBar: FC<IMonitorKeywordsTableTopBarProps> = (props) => {
    const { keywordGroup: keywordGroupProp } = props;
    const { params } = useCurrentStateAndParams();
    const {
        country,
        duration,
        webSource: websource = devicesTypes.DESKTOP,
        isWWW = false,
        sites,
        keywordsType,
    } = params;
    const [search, setSearch] = useState<string>();
    const [totalTrafficDisplayType, setTotalTrafficDisplayType] = useState<string>();
    const [extraRecommendedKeywordsCount, setExtraRecommendedKeywordsCount] = useState<number>();
    const recommendationsBodyRef = useRef<HTMLDivElement>(null);
    const [isDuplicatingInProgress, setIsDuplicatingInProgress] = useState<boolean>(false);
    const [totalTrafficLoading, setTotalTrafficLoading] = useState<boolean>();
    const [totalTrafficData, setTotalTrafficData] = useState<Pick<IMarketingWorkspaceTotalTrafficShareProps, "data">>();
    const { from, to, isWindow } = DurationService.getDurationData(duration, "", props.permissionsComponent).forAPI;

    useEffect(() => {
        calculateNumberOfHiddenItems();

        const onWindowResize = debounce(calculateNumberOfHiddenItems, 100);
        window.addEventListener("resize", onWindowResize);
        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [props.recommendations]);

    useEffect(() => {
        handleFilterChange(props);
        if (props.Keys) {
            setTotalTrafficLoading(true);
        }
    }, [country, duration, sites, websource, keywordsType, isWWW, props.Keys, props.isUsersGroup]);

    useEffect(() => {
        if (props.Keys && !totalTrafficData && !totalTrafficLoading) {
            handleFilterChange(props);
            setTotalTrafficLoading(true);
        }
    }, [props.Keys, totalTrafficData, totalTrafficLoading]);

    useEffect(() => {
        if (!totalTrafficLoading) {
            return;
        }

        (async () => {
            const { endpoint, params, headers } = getTotalTrafficEndProps();
            const data = await DefaultFetchService.getInstance().get<any>(endpoint, params, {
                headers,
            });
            if (props.isPartnerPage) {
                if (data.visits) {
                    setTotalTrafficData({
                        data: {
                            bars: transformResponse(data),
                            total: data.visits,
                            change: data.change,
                        },
                    });
                    setTotalTrafficLoading(false);
                }
            } else {
                if (data.header.visits) {
                    data.trafficDistribution = data.data.map((site) => {
                        return {
                            site: site.site,
                            value: site.value,
                            percentage: site.percentage,
                        };
                    });
                    setTotalTrafficData({
                        data: {
                            bars: transformResponse(data),
                            total: data.header.visits,
                            change: data.header.change,
                        },
                    });
                    setTotalTrafficLoading(false);
                }
            }
        })();
    }, [totalTrafficLoading]);

    const isPropChanged = (propName: string, propValue) => {
        if (props.filtersStateObject.hasOwnProperty(propName)) {
            return props.filtersStateObject[propName] !== propValue;
        } else {
            return typeof propValue !== "undefined" && propValue !== null;
        }
    };

    const handleFilterChange = (filters) => {
        let newFilters: any = {};
        if (isPropChanged("country", filters.country)) {
            newFilters = {
                ...newFilters,
                country: filters.country,
            };
        }
        if (isPropChanged("duration", filters.duration)) {
            newFilters = {
                ...newFilters,
                from,
                to,
                isWindow,
            };
        }
        if (isPropChanged("websource", filters.websource)) {
            newFilters = {
                ...newFilters,
                websource: filters.websource,
            };
        }
        if (isPropChanged("Keys", filters.Keys)) {
            newFilters = {
                ...newFilters,
                Keys: filters.Keys,
            };
        }
        if (isPropChanged("keywordsType", filters.keywordsType)) {
            newFilters = {
                ...newFilters,
                keywordsType: filters.keywordsType,
            };
        }
        if (isPropChanged("isWWW", filters.isWWW)) {
            newFilters = {
                ...newFilters,
                includeSubDomains: filters.isWWW === "*",
            };
        }

        if (newFilters.Keys) {
            props.onFilterChange(newFilters, false);
        }
    };

    const getTotalTrafficEndProps = () => {
        const sites = Array.isArray(props.Keys) ? props.Keys.join(",") : props.Keys;
        const params = {
            KeywordSource: keywordsType,
            country: country,
            Keys: props.isPartnerPage ? null : sites,
            Sites: props.isPartnerPage ? sites : null,
            from,
            to,
            isWindow,
            websource: websource,
            includeSubDomains: isWWW === "*",
            keywordGroupId: props.isPartnerPage ? keywordGroupProp.Id : null,
        };
        // remove null or undefined values from the params object
        Object.entries(params).forEach(([paramKey, ParamValue]) => {
            if (ParamValue === null || typeof ParamValue === "undefined") {
                delete params[paramKey];
            }
        });
        return {
            endpoint: props.endpoint,
            params,
            headers: NoCacheHeaders,
        };
    };

    const transformResponse = (data) => {
        return data.trafficDistribution.map(({ site, percentage, value }, index) => {
            return {
                color: CHART_COLORS.main[index],
                name: site,
                value,
                percentage,
                width: percentage * 100,
            };
        });
    };

    const calculateNumberOfHiddenItems = () => {
        if (!recommendationsBodyRef.current || !recommendationsBodyRef.current.children[0]) {
            return;
        }
        const containerOffsetTop = recommendationsBodyRef.current.offsetTop;
        const maxOffsetTop = containerOffsetTop + RECOMMENDATIONS_BODY_HEIGHT;

        const chipContainerRef = recommendationsBodyRef.current.children[0];
        const chipsRefs = Array.from<HTMLDivElement>(chipContainerRef.children as any);

        const numberOfElementsWithinContainer = chipsRefs.reduce((count, cur) => {
            return cur.offsetTop + cur.clientHeight < maxOffsetTop ? ++count : count;
        }, 0);
        setExtraRecommendedKeywordsCount(props.recommendations.length - numberOfElementsWithinContainer);
    };

    const onDisplayTypeChange = (displayType: string) => {
        setTotalTrafficDisplayType(displayType);
    };

    const onSearch = (searchTerm: string) => {
        props.onFilterChange({ filter: searchTerm }, false);
        setSearch(searchTerm);
    };

    const handleRefreshSuggestions = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `refresh/suggestions section/${keywordGroupProp.Name || ""}`);
        props.onRefreshSuggestions();
    };

    const handleShowAllRecommendations = () => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `sidebar/open recommendations sidebar/${keywordGroupProp.Name || ""}`,
        );
        props.onClickRecommended?.();
    };

    const chipsItems = props.recommendations.map((chip, index) => {
        return {
            text: chip.keyword,
            cpc: chip.cpc,
            volume: chip.volume,
            key: chip.keyword.split(" ").join("_"),
            displayTooltipForTextLongerThan: -1, // Disables tooltip
            isSelected: false,
            onClick: () => {
                allTrackers.dispatchTrackEvent(
                    "Button",
                    "submit-ok",
                    `add recommendation/suggestions section/${keywordGroupProp.Name}`,
                );
                props.onAddKeywordClick([chip.keyword], index)();
            },
        };
    });

    // the parent width divided by the number of loaders in each row minus its margins
    const recommendationsLoaderWidth =
        (recommendationsBodyRef.current?.clientWidth ?? 0) / (NUMBER_OF_LOADERS / 2) - LOADER_HORIZONTAL_MARGIN;

    const handleDuplicateKeywordGroup = async (groupId: string, groupName: string) => {
        setIsDuplicatingInProgress(true);
        try {
            const group = await keywordGroupsQueryClient.duplicateGroup({
                KeywordGroupId: groupId,
                KeywordGroupNewName: groupName,
            });
            const current = proNavigator.current();
            void proNavigator.go(current, { keywordGroupId: group.Id }, { reload: true });
        } catch (e) {
            /* empty */
        } finally {
            setIsDuplicatingInProgress(false);
        }
    };

    return (
        <div>
            {props.Keys ? (
                <TotalTrafficShareContainer>
                    {totalTrafficData ? (
                        <MarketingWorkspaceTotalTrafficShare
                            title={props.totalTrafficTitle}
                            data={totalTrafficData ? totalTrafficData.data : null}
                            loading={totalTrafficLoading}
                            setDisplayType={onDisplayTypeChange}
                            displayType={totalTrafficDisplayType}
                            tooltipTitle={props.tooltipTitle}
                        />
                    ) : null}
                </TotalTrafficShareContainer>
            ) : null}
            {props.isRecommendationOpen ||
            !props.isUsersGroup ||
            (!props.isRecommendationLoading && !props.recommendations.length) ? null : (
                <MonitorKeywordsSuggestedKeywordsContainer>
                    <BoxTitle
                        Container={BoxTitleContainer}
                        iconName="wand"
                        tooltip={translate("workspace.marketing.keywordgroup.topbar.recommendations.tooltip")}
                        customElement={
                            !props.isRecommendationLoading ? (
                                <BoxTitleRightAlignedContainer>
                                    <PlainTooltip
                                        tooltipContent={translate(
                                            "workspace.marketing.keywordgroup.topbar.recommendations.refresh.tooltip",
                                        )}
                                        placement="top"
                                    >
                                        <div>
                                            <IconButton
                                                type="flat"
                                                iconName="refresh"
                                                onClick={handleRefreshSuggestions}
                                            />
                                        </div>
                                    </PlainTooltip>
                                    {extraRecommendedKeywordsCount ? (
                                        <Button
                                            type="flat"
                                            label={translate(
                                                "workspace.marketing.keywordgroup.topbar.recommendations.more.button",
                                                {
                                                    numberOfKeywords: props.recommendations.length,
                                                },
                                            )}
                                            onClick={handleShowAllRecommendations}
                                        ></Button>
                                    ) : null}
                                </BoxTitleRightAlignedContainer>
                            ) : undefined
                        }
                    >
                        {translate("workspace.marketing.keywordgroup.topbar.recommendations.title")}
                    </BoxTitle>
                    <RecommendationsBody ref={recommendationsBodyRef}>
                        {props.isRecommendationLoading ? (
                            <RecommendationsLoaderContainer>
                                {Array(NUMBER_OF_LOADERS)
                                    .fill(undefined)
                                    .map((_, idx) => (
                                        <PixelPlaceholderLoaderStyled
                                            width={recommendationsLoaderWidth}
                                            height={32}
                                            key={idx}
                                        />
                                    ))}
                            </RecommendationsLoaderContainer>
                        ) : (
                            <ChipContainer itemsComponent={withTooltip(ButtonChipItem)}>{chipsItems}</ChipContainer>
                        )}
                    </RecommendationsBody>
                </MonitorKeywordsSuggestedKeywordsContainer>
            )}
            <MonitorKeywordsTableTopBarContainer>
                <SearchContainerWrapper hideBorder={false}>
                    <SearchInput
                        defaultValue={search}
                        debounce={400}
                        onChange={onSearch}
                        placeholder={translate(props.searchPlaceholder)}
                    />
                    <Right>
                        <OptionButtonsContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                excelBodyForReq={props.excelConfig.requestBody}
                                downloadUrl={props.excelConfig.url}
                            ></DownloadButtonMenu>
                            {!props.isUsersGroup ? (
                                <PlainTooltip
                                    tooltipContent={translate("keyword.groups.sharing.duplicate.tooltip")}
                                    placement="top"
                                >
                                    <span>
                                        <OptionButton
                                            isLoading={isDuplicatingInProgress}
                                            type={buttonTypes.FLAT}
                                            iconName="copy-and-edit"
                                            onClick={() => {
                                                void handleDuplicateKeywordGroup(
                                                    keywordGroupProp.Id,
                                                    keywordGroupProp.Name,
                                                );
                                            }}
                                        />
                                    </span>
                                </PlainTooltip>
                            ) : null}
                        </OptionButtonsContainer>
                    </Right>
                </SearchContainerWrapper>
            </MonitorKeywordsTableTopBarContainer>
        </div>
    );
};

const withTooltip = (Component) =>
    function (props: IChipItem & { volume: string; cpc: string }) {
        return (
            <PlainTooltip
                variation="white"
                tooltipContent={
                    <TooltipContentContainer>
                        <TooltipColumn>
                            <I18n>workspace.marketing.keywordgroup.topbar.recommendations.chip.volume</I18n>
                            <TooltipValue>{props.volume}</TooltipValue>
                        </TooltipColumn>
                        <TooltipColumnSeparator />
                        <TooltipColumn>
                            <I18n>workspace.marketing.keywordgroup.topbar.recommendations.chip.cpc</I18n>
                            <TooltipValue>{props.cpc}</TooltipValue>
                        </TooltipColumn>
                    </TooltipContentContainer>
                }
                placement="top"
            >
                <div>
                    <Component {...props}></Component>
                </div>
            </PlainTooltip>
        );
    };
